import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Ticket, Users, AlertCircle, CheckCircle } from 'lucide-react';
import { dashboardService } from '../../services/dashboardService';
import { ticketService } from '../../services/ticketService';

const Dashboard = () => {
  const [stats, setStats] = useState({
    total_tickets: 0,
    new_tickets: 0,
    in_progress: 0,
    completed: 0
  });
  const [recentTickets, setRecentTickets] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsData, ticketsData] = await Promise.all([
        dashboardService.getStats(),
        ticketService.getAll()
      ]);
      setStats(statsData);
      
      // Filtrar apenas chamados sem responsável atribuído e com status 'novo'
      // Ordenar do mais recente para o menos recente
      const newTickets = ticketsData
        .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
        .slice(0, 5);
      
      setRecentTickets(newTickets);
    } catch (error) {
      console.error('Erro ao buscar dados:', error);
    } finally {
      setLoading(false);
    }
  };

  const statsCards = [
    {
      title: 'Total de Chamados',
      value: stats.total_tickets,
      icon: Ticket,
      color: 'text-blue-600'
    },
    {
      title: 'Novos',
      value: stats.new_tickets,
      icon: AlertCircle,
      color: 'text-yellow-600'
    },
    {
      title: 'Em Andamento',
      value: stats.in_progress,
      icon: Users,
      color: 'text-blue-600'
    },
    {
      title: 'Concluídos',
      value: stats.completed,
      icon: CheckCircle,
      color: 'text-green-600'
    }
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Dashboard</h1>
        <p className="text-gray-600 mt-1">Visão geral do sistema</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {loading ? (
          <div className="col-span-4 text-center py-8">Carregando...</div>
        ) : (
          statsCards.map((stat, index) => {
            const IconComponent = stat.icon;
            return (
              <Card key={index}>
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-sm font-medium text-gray-600">
                    {stat.title}
                  </CardTitle>
                  <IconComponent className={`w-5 h-5 ${stat.color}`} />
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold">{stat.value}</div>
                </CardContent>
              </Card>
            );
          })
        )}
      </div>

      {/* Recent Tickets */}
      <Card>
        <CardHeader>
          <CardTitle>Chamados Recentes</CardTitle>
          <CardDescription>Ultimos chamados abertos no sistema</CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8">Carregando...</div>
          ) : (
            <div className="space-y-4">
              {recentTickets.map((ticket) => (
                <div
                  key={ticket._id}
                  className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <div>
                    <h4 className="font-semibold text-gray-900">{ticket.title}</h4>
                    <p className="text-sm text-gray-600">{ticket.description}</p>
                  </div>
                  <div className="text-right">
                    <span
                      className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${
                        ticket.status === 'novo'
                          ? 'bg-yellow-100 text-yellow-800'
                          : ticket.status === 'em_andamento'
                          ? 'bg-blue-100 text-blue-800'
                          : 'bg-green-100 text-green-800'
                      }`}
                    >
                      {ticket.status === 'novo' ? 'Novo' : ticket.status === 'em_andamento' ? 'Em Andamento' : 'Concluído'}
                    </span>
                    <p className="text-xs text-gray-500 mt-1">
                      {new Date(ticket.created_at).toLocaleDateString('pt-BR')}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Dashboard;